import React from "react";
import { ArrowLeft, Music, Heart, Users, Zap } from "lucide-react";
import { Button } from "./ui/button";

export const AboutPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 sticky top-0 bg-background/95 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={onBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl sm:text-5xl text-street tracking-wide mb-6">
          À propos de TIKA
        </h1>
        
        <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
          La plateforme de billetterie qui célèbre la culture urbaine
        </p>

        {/* Mission Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="glass-card rounded-xl p-8 border-l-4 border-pink-500">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center mb-4">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-3">Notre Mission</h3>
            <p className="text-muted-foreground leading-relaxed">
              Connecter les passionnés de musique urbaine avec les meilleurs événements Hip-Hop/RNB, Afrobeat et Dancehall. 
              Nous croyons en la puissance de la musique pour rassembler les communautés.
            </p>
          </div>

          <div className="glass-card rounded-xl p-8 border-l-4 border-emerald-500">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center mb-4">
              <Music className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-3">Notre Vision</h3>
            <p className="text-muted-foreground leading-relaxed">
              Devenir la référence incontournable pour découvrir et vivre des expériences musicales authentiques. 
              Promouvoir les artistes et créer des moments inoubliables.
            </p>
          </div>

          <div className="glass-card rounded-xl p-8 border-l-4 border-blue-500">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-3">Notre Communauté</h3>
            <p className="text-muted-foreground leading-relaxed">
              Une communauté diverse et passionnée qui partage l'amour de la musique urbaine. 
              Ensemble, nous célébrons la richesse culturelle et créons des connexions authentiques.
            </p>
          </div>

          <div className="glass-card rounded-xl p-8 border-l-4 border-primary">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-primary-foreground" />
            </div>
            <h3 className="text-xl font-bold mb-3">Notre Engagement</h3>
            <p className="text-muted-foreground leading-relaxed">
              Offrir une expérience de billetterie simple, rapide et sécurisée. 
              Soutenir les artistes émergents et établis, et rendre la culture accessible à tous.
            </p>
          </div>
        </div>

        {/* Story Section */}
        <div className="glass-card rounded-xl p-8 mb-12">
          <h2 className="text-2xl font-bold mb-6">Notre Histoire</h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              TIKA est née de la passion pour la musique urbaine et du désir de créer une plateforme 
              qui met en avant la richesse culturelle du Hip-Hop, de l'Afrobeat et du Dancehall.
            </p>
            <p>
              Nous avons constaté qu'il manquait un espace dédié où les fans pouvaient découvrir 
              facilement les événements qui résonnent avec leur identité culturelle. TIKA comble ce vide 
              en offrant bien plus qu'une simple billetterie : c'est une célébration de notre culture.
            </p>
            <p>
              Aujourd'hui, nous travaillons avec des artistes, des promoteurs et des salles à travers 
              le pays pour offrir des expériences musicales exceptionnelles et accessibles à tous.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="glass-card rounded-xl p-8">
          <h2 className="text-2xl font-bold mb-6">Nos Valeurs</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-pink-500"></span>
                Authenticité
              </h4>
              <p className="text-sm text-muted-foreground">
                Promouvoir une culture musicale authentique et respectueuse de ses racines
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                Inclusion
              </h4>
              <p className="text-sm text-muted-foreground">
                Créer un espace accueillant pour tous les amateurs de musique urbaine
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                Innovation
              </h4>
              <p className="text-sm text-muted-foreground">
                Offrir une expérience moderne et fluide grâce à la technologie
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary"></span>
                Passion
              </h4>
              <p className="text-sm text-muted-foreground">
                Mettre le cœur au centre de tout ce que nous faisons
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
